<template>
    <BaseFormField
        class="event-waiting-list-button"
        :errors="[errorMessage]"
    >
        <BaseButton
            type="button"
            :class="{ 'button--secondary': defaultStyling }"
            :disabled="isSubmitting"
            @click="toggleReservation"
        >
            <template #default>
                <template v-if="isInWaitingList">Afmelden wachtlijst</template>
                <template v-else>Aanmelden wachtlijst</template>
            </template>
        </BaseButton>
    </BaseFormField>
</template>

<script setup lang="ts">
import { STATUS } from '~/constants/errorMessages';

const props = withDefaults(
    defineProps<{
        eventId: number;
        eventTitle: string;
        eventVenueName: string | null;
        defaultStyling?: boolean;
    }>(),
    {
        defaultStyling: true,
    }
);

const isSubmitting = ref(false);
const errorMessage = ref<string|null>(null);

const { waitingList } = storeToRefs(useUserStore());

const isInWaitingList = computed(() => waitingList.value.includes(props.eventId));

const joinWaitingListConfirmText = computed(() => {
    if (props.eventTitle) {
        return 'Je meldt je aan voor de wachtlijst van ' +
            props.eventTitle +
            '. Dit is de online We Are Public wachtlijst en niet geldig aan de kassa van ' +
            props.eventVenueName +
            '.';
    }

    return 'Weet je zeker dat je je wilt aanmelden voor de wachtlijst?';
});

const leaveWaitingListConfirmText = computed(() => {
    if (props.eventTitle) {
        return `Weet je zeker dat je je wachtlijst aanmelding voor ${props.eventTitle} wilt annuleren?`;
    }

    return 'Weet je zeker dat je je wachtlijst aanmelding voor dit programma wilt annuleren?';
});

const toggleReservation = async() => {
    if (isInWaitingList.value) {
        await leaveWaitingList();
    } else {
        await joinWaitingList();
    }
};

const eventsStore = useEventsStore();

const joinWaitingList = async() => {
    const confirmed = confirm(joinWaitingListConfirmText.value);

    if (!confirmed) {
        return;
    }

    isSubmitting.value = true;

    const { error } = await useFetchLaravel(
        `/v2.0/user/me/waiting-list/${props.eventId}`,
        {
            method: 'POST',
        }
    );

    if (error.value) {
        errorMessage.value = STATUS['500'];
    } else {
        // Refresh the user waiting list, so we have the most recent waiting lists.
        await refreshNuxtData(USER_STORE_DATA_KEY_USER_WAITING_LISTS);

        useToastsStore().addMessage({
            title: 'Je hebt je aangemeld voor de wachtlijst',
            message: '',
            status: 'success'
        });

        eventsStore.updateMyListHome();
    }

    isSubmitting.value = false;
};

const leaveWaitingList = async() => {
    const confirmed = confirm(leaveWaitingListConfirmText.value);

    if (!confirmed) {
        return;
    }

    isSubmitting.value = true;

    const { error } = await useFetchLaravel(
        `/v2.0/user/me/waiting-list/${props.eventId}`,
        {
            method: 'DELETE'
        }
    );

    if (error.value) {
        errorMessage.value = STATUS['500'];
    } else {
        // Refresh the user waiting list, so we have the most recent waiting lists.
        await refreshNuxtData(USER_STORE_DATA_KEY_USER_WAITING_LISTS);

        useToastsStore().addMessage({
            title: 'Je hebt je afgemeld voor de wachtlijst',
            message: '',
            status: 'success'
        });

        eventsStore.updateMyListHome();
    }

    isSubmitting.value = false;
};
</script>

<style src="./EventWaitingListButton.less" lang="less"></style>
